import { action, computed } from "mobx";
import http from "@app/lib/http";
import BaseStore from "./base";
import SessionStore from "./session";
import ApplicationSettings from "@app/state/model/application/settings";
import notify from "@app/components/notify/index";
import { AI_TEMPLATES } from "@app/constants";

export class Application extends BaseStore {
    observable() {
        return {
            settings: null,
            loading: false,
        };
    }

    @computed get enableAI() {
        return this.settings?.ai?.enabled && SessionStore.can("copilot");
    }

    @computed get enableArticleGalaxy() {
        return this.settings?.articleGalaxy?.enabled;
    }

    // Without the integration configured a run could be set up but never started,
    // so the AI template options are hidden entirely.
    @computed get enableCer() {
        return this.settings?.cer?.enabled;
    }

    @computed get enabledAiTemplates() {
        if (!this.enableCer) {
            return [];
        }

        const enabledCategories = this.settings.cer.templates || {};
        return AI_TEMPLATES.filter((template) => enabledCategories[template.category]);
    }

    @action
    async load() {
        if (this.settings) {
            return;
        }

        try {
            this.loading = true;

            const { data } = await http.get(`/application/settings`);

            let ssoSettings = this.loadSSOSettingsFromLocalStorage();
            if (!ssoSettings && data.auth) {
                ssoSettings = data.auth;
                this.saveSSOSettingsToLocalStorage(ssoSettings);
            }

            if (ssoSettings.sso && !data.auth.sso) {
                this.clearSSOSettingsFromLocalStorage();
            }

            this.settings = new ApplicationSettings({
                ...data,
                sso: data.auth?.sso || null,
            });

            this.loading = false;
        } catch (error) {
            this.loading = false;
            notify.error(error.response?.data?.error || "Failed to load application settings");
        }
    }

    @action
    setSSOSettings(ssoConfig) {
        if (!this.settings) {
            this.settings = new ApplicationSettings({});
        }
        this.settings.sso = {
            ...this.settings.sso,
            ...ssoConfig,
        };
        this.saveSSOSettingsToLocalStorage();
    }

    saveSSOSettingsToLocalStorage() {
        if (this.settings?.sso) {
            localStorage.setItem("ssoSettings", JSON.stringify(this.settings.sso));
        }
    }

    loadSSOSettingsFromLocalStorage() {
        const ssoSettings = localStorage.getItem("ssoSettings");
        if (ssoSettings) {
            return { sso: JSON.parse(ssoSettings) };
        }

        return null;
    }

    clearSSOSettingsFromLocalStorage() {
        localStorage.removeItem("ssoSettings");
    }
}

export default new Application();
